// ============================================
// FRONTEND: hooks/useFeedBatches.js
// Rolling Window Infinite Scroll Hook with Client-Side Cache
// FIXED: Focus preservation by event ID + two-way scroll adjustment
// ============================================

import { useState, useRef, useCallback, useEffect } from 'react';
import api from '../utils/axiosConfig';
import normalizeEvent from '../utils/normalizeEvent';
import EventService from '../services/eventService';

// INSTAGRAM-STYLE: Larger buffers for seamless infinite scroll
// Backend over-fetches (returns up to 30 items per request), so we increase batch size to match
const BUFFER_SIZE = 90;            // Total items in memory (1.5x larger - can hold 3 full batches)
const WINDOW_SIZE = 30;            // Items actively rendered (unchanged - good balance)
const BATCH_SIZE = 15;             // Load 15 items at a time (50% more - matches backend better)
const TRIGGER_THRESHOLD_DOWN = 23; // Load next when at index 23 (7 from bottom of 30-item window)
const TRIGGER_THRESHOLD_UP = 7;    // Load previous when at index 7 (7 from top)
const TRIM_THRESHOLD = 120;        // Trim cache when buffer exceeds 120 items (increased)
const TRIM_AMOUNT = 30;            // Remove 30 items when trimming (1 full batch)

/**
 * Rolling Window Infinite Scroll Hook with Client-Side Cache
 *
 * Maintains a fixed window of 15 events, loading 5 at a time bidirectionally
 * Uses client-side cache since the backend API only supports forward pagination
 *
 * @param {Object} config - Configuration
 * @param {string} config.userId - User ID for personalization
 * @param {string} config.language - Language preference
 * @param {number} config.lat - User latitude
 * @param {number} config.lng - User longitude
 * @param {boolean} config.enabled - Enable/disable the hook
 */
export const useFeedBatches = ({
  userId = null,
  language = 'EN',
  lat = null,
  lng = null,
  enabled = true,
  searchQuery = '',   // NEW: optional text search
  category = null,    // NEW: optional category filter
  startDate = null,   // NEW: optional start date filter (YYYY-MM-DD)
  endDate = null,     // NEW: optional end date filter (YYYY-MM-DD)
  source = ['events'],        // NEW: Which collections to search
  excludeCategories = [],     // NEW: Categories to exclude
  radiusKm = 50       // NEW: Radius in km for geo filter
} = {}) => {
  // State
  const [items, setItems] = useState([]);
  const [isLoadingNext, setIsLoadingNext] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [focusIndex, setFocusIndex] = useState(7); // Center item (index 7 of 15)
  const [focusIsAuthoritative, setFocusIsAuthoritative] = useState(true); // NEW: Whether focus was truly preserved
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isShiftingWindow, setIsShiftingWindow] = useState(false); // NEW: Prevent interference during shifts
  const [pendingScrollAdjustment, setPendingScrollAdjustment] = useState(null); // NEW: Two-way scroll compensation

  // Client-side cache for all loaded events
  const eventCacheRef = useRef([]); // Stores all events loaded so far
  const windowStartIndexRef = useRef(0); // Index in cache where current window starts

  // Refs for API cursor and deduplication
  const nextCursorRef = useRef(null);
  const loadingLockRef = useRef(false);
  const focusedEventIdRef = useRef(null);
  const focusedIndexRef = useRef(7); // Track current focus index for math-based preservation
  const currentFocusEventIdRef = useRef(null); // TASK 1: Track event ID for focus preservation
  const focusIsAuthoritativeRef = useRef(true); // NEW: Track if current focus is authoritative
  const seenIdsRef = useRef(new Set()); // Track seen event IDs
  const recentlySeenIdsRef = useRef(new Set()); // Track recently seen IDs (last 50)
  const scrollAdjustmentIdRef = useRef(0); // Unique ID for each adjustment
  const lastShiftPreservedByEventIdRef = useRef(false); // Part 3: Track if last shift preserved by event ID
  const wrapCycleCountRef = useRef(0); // Track how many times we've wrapped/refreshed
  const isInRotationModeRef = useRef(false); // Track if we're rotating (true) or still loading new content (false)

  /**
   * Hydrate events with PostgreSQL images by batch fetching
   */
  const hydrateEventsWithImages = useCallback(async (events) => {
    if (!events || events.length === 0) return events;

    try {
      // Split events and reviews - only hydrate events from PostgreSQL
      const eventItems = events.filter(e => e._source !== 'review');
      const reviewItems = events.filter(e => e._source === 'review');

      // Extract event IDs (skip reviews)
      // FIX: only send numeric IDs — the feed also contains venues (_source: 'venue')
      // whose UUID ids the /events/batch endpoint rejects; a page with zero numeric
      // IDs previously caused a 400 Bad Request on every hydration call.
      const eventIds = eventItems
        .map(e => e.event_id || e.id)
        .filter(Boolean)
        .filter(id => /^\d+$/.test(String(id)));

      if (eventIds.length === 0) {
        // Nothing hydratable (only reviews/venues), no hydration needed
        return events.map(e => ({ ...e, _imageSource: e._source === 'review' ? 'review' : 'typesense' }));
      }

      console.log('🖼️ [useFeedBatches] Hydrating images for', eventIds.length, 'events from PostgreSQL (skipping', reviewItems.length, 'reviews)');

      // Batch fetch full event data from PostgreSQL
      const pgEvents = await EventService.getBatchEvents(eventIds);

      if (!pgEvents || pgEvents.length === 0) {
        console.warn('⚠️ [useFeedBatches] No events returned from PostgreSQL batch fetch');
        return events;
      }

      // Create a map of event_id -> PostgreSQL fields needed by map/list UI.
      // The feed may start from Typesense-only docs, so keep creator metadata
      // from the canonical events batch response as well as image fields.
      const eventDataMap = new Map();
      pgEvents.forEach(pgEvent => {
        const id = String(pgEvent.event_id || pgEvent.id);
        if (id) {
          eventDataMap.set(id, pgEvent);
        }
      });

      console.log('🎨 [useFeedBatches] Created PostgreSQL event map with', eventDataMap.size, 'entries');

      // Merge PostgreSQL images back into events; pass reviews through as-is
      const hydratedEvents = events.map(event => {
        // Reviews already have their image from Typesense/backend normalization
        if (event._source === 'review') {
          return { ...event, _imageSource: 'review' };
        }

        const eventId = String(event.event_id || event.id);
        const pgEvent = eventDataMap.get(eventId);
        const pgImage = pgEvent?.event_image_link || pgEvent?.image_url || pgEvent?.poster_url;

        if (pgEvent) {
          const additionalImages = Array.isArray(pgEvent.additional_image_links) && pgEvent.additional_image_links.length > 0
            ? pgEvent.additional_image_links
            : event.additional_image_links ?? [];

          return {
            ...event,
            event_creator_id: pgEvent.event_creator_id ?? event.event_creator_id,
            creator_name: pgEvent.creator_name ?? event.creator_name,
            user_name: pgEvent.user_name ?? event.user_name,
            event_image_link: pgImage || event.event_image_link,
            image_url: pgImage || event.image_url,
            additional_image_links: additionalImages,
            _imageSource: pgImage ? 'postgresql' : 'typesense'
          };
        }

        // Keep original if no PostgreSQL image found
        return {
          ...event,
          _imageSource: 'typesense' // Debug flag
        };
      });

      // Log hydration results
      const hydratedCount = hydratedEvents.filter(e => e._imageSource === 'postgresql').length;
      console.log('✅ [useFeedBatches] Hydrated', hydratedCount, '/', events.length, 'images from PostgreSQL');

      return hydratedEvents;
    } catch (error) {
      console.error('❌ [useFeedBatches] Image hydration failed:', error);
      // Return original events on error
      return events;
    }
  }, []);

  /**
   * Fetch events from API using POST /events/search/feed
   * Then hydrate with PostgreSQL images
   * INFINITE SCROLL: Manages wrap_refresh cycles to never run out of content
   * @param {string|null} cursor - Pagination cursor
   * @param {number} limit - Number of items to fetch
   * @param {boolean} skipExclusions - If true, don't send exclude_ids (used during prefetch)
   */
  const fetchEvents = useCallback(async (cursor = null, limit = BATCH_SIZE, skipExclusions = false) => {
    try {
      // INFINITE SCROLL: Handle wrap_refresh cursor with smart rotation
      let actualCursor = cursor;
      let excludeIds = [];

      // Skip exclusions during prefetch OR when loading new content from database
      if (skipExclusions || !isInRotationModeRef.current) {
        if (skipExclusions) {
          console.log('📋 [useFeedBatches] Prefetch mode - fetching without exclusions');
        } else {
          console.log('📋 [useFeedBatches] Loading new content - minimal exclusions (last 10 items only)');
          // Still exclude the very last items to avoid immediate repeats
          const recentArray = Array.from(recentlySeenIdsRef.current);
          excludeIds = recentArray.slice(-10);
        }
      } else if (cursor === 'wrap_refresh') {
        console.log('🔄 [useFeedBatches] Wrap refresh detected - using smart rotation strategy');
        actualCursor = null; // Reset to page 1
        wrapCycleCountRef.current += 1;

        // INSTAGRAM-STYLE ROTATION: For small pools, use very aggressive exclusion reduction
        const allRecentlySeen = Array.from(recentlySeenIdsRef.current);
        const cacheSize = eventCacheRef.current.length;

        console.log('📋 [useFeedBatches] Wrap cycle #' + wrapCycleCountRef.current + ' - Recently seen:', allRecentlySeen.length, 'items, cache:', cacheSize);

        // CRITICAL FIX: For small pools, exclude only the LAST few items (not a ratio)
        // This ensures we get fresh items even with pools of 25-50 items
        if (wrapCycleCountRef.current === 1) {
          // First wrap: Exclude only last 5 items (maximum freshness)
          excludeIds = allRecentlySeen.slice(-Math.min(5, allRecentlySeen.length));
          console.log('📋 [useFeedBatches] First wrap - excluding only last', excludeIds.length, 'items for maximum variety');
        } else if (wrapCycleCountRef.current % 3 === 0) {
          // Every 3rd wrap: Clear most exclusions for full refresh
          excludeIds = [];
          recentlySeenIdsRef.current = new Set();
          console.log('📋 [useFeedBatches] Periodic full refresh - cleared all exclusions');
        } else {
          // Other wraps: Exclude last 10 items
          excludeIds = allRecentlySeen.slice(-Math.min(10, allRecentlySeen.length));
          console.log('📋 [useFeedBatches] Subsequent wrap - excluding last', excludeIds.length, 'items');
        }
      } else {
        // Normal fetch - exclude recently seen items
        excludeIds = Array.from(recentlySeenIdsRef.current);
      }

      // Build request body for POST /events/search/feed
      const requestBody = {
        limit: limit,
        cursor: actualCursor,
        allow_langs: language ? [language] : ['TH', 'EN'],
        userId: userId || null,
        exclude_ids: excludeIds,
      };

      // NEW: optional text search using same field name as Typesense
      if (searchQuery && searchQuery.trim()) {
        requestBody.q = searchQuery.trim();
      }

      // NEW: optional category filter
      if (category && category.trim()) {
        requestBody.category = category.trim();
      }

      // NEW: optional date range filter (YYYY-MM-DD format)
      if (startDate) {
        requestBody.startDate = startDate;
      }
      if (endDate) {
        requestBody.endDate = endDate;
      }

      // NEW: source and excludeCategories for multi-collection search
      if (source && Array.isArray(source)) {
        requestBody.source = source;
      }
      if (excludeCategories && Array.isArray(excludeCategories) && excludeCategories.length > 0) {
        requestBody.excludeCategories = excludeCategories;
      }

      // Add geo filter if location is provided
      if (lat && lng) {
        requestBody.geo = {
          lat: parseFloat(lat),
          lng: parseFloat(lng),
          radiusKm: parseFloat(radiusKm)
        };
      }

      console.log('[useFeedBatches] Fetching with cursor:', cursor, 'limit:', limit);
      console.log('🔍 [useFeedBatches] Request body:', JSON.stringify(requestBody, null, 2));

      const response = await api.post('/events/search/feed', requestBody);

      if (response.data && Array.isArray(response.data.items)) {
        console.log('📦 [useFeedBatches] Received', response.data.items.length, 'raw items from Typesense');

        // Normalize events first
        const normalizedEvents = response.data.items.map(normalizeEvent);

        // Hydrate with PostgreSQL images
        const hydratedEvents = await hydrateEventsWithImages(normalizedEvents);

        // Log first item AFTER hydration
        if (hydratedEvents.length > 0) {
          console.log('✨ [useFeedBatches] First item after hydration:', {
            id: hydratedEvents[0].event_id,
            name: hydratedEvents[0].event_name?.substring(0, 30),
            image: hydratedEvents[0].event_image_link?.substring(0, 60),
            source: hydratedEvents[0]._imageSource,
            // Check category fields for map marker icons
            type: hydratedEvents[0].type,
            main_category: hydratedEvents[0].main_category,
            category: hydratedEvents[0].category
          });
        }

        // Add to seen IDs
        // During prefetch (skipExclusions=true), only track in full history, not in recent
        // This allows all prefetched items to appear in rotation
        hydratedEvents.forEach(event => {
          const eventId = String(event.event_id || event.id);
          if (eventId) {
            seenIdsRef.current.add(eventId); // Full history

            // Only add to recently seen if not in prefetch mode
            if (!skipExclusions) {
              recentlySeenIdsRef.current.add(eventId); // Recent window for deduplication
            }
          }
        });

        // Trim recently seen if it gets too large (keep last 100)
        if (!skipExclusions && recentlySeenIdsRef.current.size > 100) {
          const recentArray = Array.from(recentlySeenIdsRef.current);
          recentlySeenIdsRef.current = new Set(recentArray.slice(-100));
          console.log('🗑️ [useFeedBatches] Trimmed recently seen to 100 items');
        }

        // INFINITE SCROLL: Handle wrap_cycle from server
        // Only wrap when server explicitly signals wrap_cycle AND we have no more pages
        let finalNextCursor = response.data.next_cursor;

        if (response.data.wrap_cycle > 0) {
          console.log('🔄 [useFeedBatches] Server signaled wrap_cycle:', response.data.wrap_cycle);

          // If we got items, continue with the provided cursor (might have more pages)
          if (hydratedEvents.length > 0 && response.data.next_cursor) {
            console.log('📄 [useFeedBatches] Got', hydratedEvents.length, 'items with wrap signal - will continue to next page:', response.data.next_cursor);
            finalNextCursor = response.data.next_cursor;
          }
          // If no items AND no cursor, truly exhausted - time to wrap
          else if (hydratedEvents.length === 0) {
            console.log('🔄 [useFeedBatches] Pool exhausted (0 items, no cursor) - entering rotation mode');
            isInRotationModeRef.current = true; // Switch to rotation mode
            finalNextCursor = 'wrap_refresh';
          }
        }

        return {
          events: hydratedEvents,
          nextCursor: finalNextCursor,
          hasMore: !!finalNextCursor
        };
      }

      return { events: [], nextCursor: null, hasMore: false };
    } catch (err) {
      console.error('[useFeedBatches] Fetch error:', err);
      throw err;
    }
  }, [language, userId, lat, lng, searchQuery, category, startDate, endDate, source, excludeCategories, hydrateEventsWithImages]);

  /**
   * Update the visible window from cache
   * TASK 1: Enhanced to support event-ID-based preservation first, then index-based fallback
   */
  const updateWindowFromCache = useCallback((startIndex, preserveFocusIndex = null, preserveFocusEventId = null) => {
    const cache = eventCacheRef.current;

    // CRITICAL FIX: Allow window to shift beyond current cache size
    // The old formula would clamp to 0 when cache is smaller than WINDOW_SIZE
    const windowStart = Math.max(0, Math.min(startIndex, Math.max(0, cache.length - 1)));
    const windowEnd = Math.min(windowStart + WINDOW_SIZE, cache.length);
    const windowItems = cache.slice(windowStart, windowEnd);

    console.log('[useFeedBatches] 📊 Window calculation:', {
      requestedStart: startIndex,
      actualStart: windowStart,
      cacheLength: cache.length,
      windowSize: WINDOW_SIZE,
      itemsInWindow: windowItems.length
    });

    windowStartIndexRef.current = windowStart;
    setItems(windowItems);

    let newFocusIndex = null;
    let preservedByEventId = false;
    let preservedByIndexMath = false;

    // Method 1: Preserve by event ID first (most reliable)
    const anchorId = preserveFocusEventId || currentFocusEventIdRef.current;

    if (anchorId) {
      const foundIndex = windowItems.findIndex((ev) => {
        const evId = String(ev.id ?? ev.event_id ?? '');
        return evId === String(anchorId);
      });

      if (foundIndex >= 0) {
        newFocusIndex = foundIndex;
        preservedByEventId = true;
        lastShiftPreservedByEventIdRef.current = true; // Part 3: Track this preservation
        console.log('[useFeedBatches] ✅ Preserved focus by EVENT ID:', anchorId, '→ index', foundIndex);
      }
    }

    // Method 2: Fallback to index-based preservation
    if (newFocusIndex === null && preserveFocusIndex !== null && preserveFocusIndex >= 0 && preserveFocusIndex < windowItems.length) {
      newFocusIndex = preserveFocusIndex;
      preservedByIndexMath = true;
      lastShiftPreservedByEventIdRef.current = false; // Part 3: Not preserved by event ID
      console.log('[useFeedBatches] ✅ Preserved focus by INDEX math → index', preserveFocusIndex);
    }

    // Method 3: Final fallback - center the focus
    if (newFocusIndex === null) {
      newFocusIndex = Math.floor(windowItems.length / 2);
      lastShiftPreservedByEventIdRef.current = false; // Part 3: Not preserved by event ID
      console.log('[useFeedBatches] ⚠️ Using fallback center focus at index:', newFocusIndex);
    }

    // Determine if this focus is authoritative (safe to anchor to)
    let isAuthoritativeFocus = false;

    // if we found the same eventId in the new window, that's authoritative
    if (preservedByEventId) {
      isAuthoritativeFocus = true;
    }

    // if we fell back to index math AND the resulting index is inside the displayed window, accept it
    if (!isAuthoritativeFocus && preservedByIndexMath) {
      if (newFocusIndex >= 0 && newFocusIndex < windowItems.length) {
        isAuthoritativeFocus = true;
      }
    }

    // if we ended up with 0 or center because window wasn't ready, leave isAuthoritativeFocus = false
    console.log('[useFeedBatches] Focus quality:', {
      index: newFocusIndex,
      isAuthoritative: isAuthoritativeFocus,
      method: preservedByEventId ? 'eventId' : preservedByIndexMath ? 'indexMath' : 'fallback'
    });

    setFocusIndex(newFocusIndex);
    setFocusIsAuthoritative(isAuthoritativeFocus);
    focusedIndexRef.current = newFocusIndex;
    focusIsAuthoritativeRef.current = isAuthoritativeFocus;

    // Update current focus event ID ref
    if (windowItems[newFocusIndex]) {
      currentFocusEventIdRef.current = String(windowItems[newFocusIndex].id ?? windowItems[newFocusIndex].event_id ?? '');
    }

    console.log('[useFeedBatches] Window updated:', {
      windowStart,
      windowEnd,
      displayedItems: windowItems.length,
      totalCached: cache.length,
      focusIndex: newFocusIndex,
      focusIsAuthoritative: isAuthoritativeFocus
    });
  }, []);

  /**
   * Initialize feed - IMPROVED: Fetch multiple pages to build a larger initial cache
   * This ensures we have more variety before starting to rotate
   */
  const initialize = useCallback(async () => {
    if (!enabled || isInitialized) return;

    console.log('[useFeedBatches] Initializing feed... (lat:', lat, 'lng:', lng, ')');
    setIsLoadingNext(true);
    setError(null);
    isInRotationModeRef.current = false;

    try {
      const MAX_PREFETCH_PAGES = 5;

      // PHASE 1: Fetch page 1 and show it immediately so users see content fast
      const firstBatch = await fetchEvents(null, BATCH_SIZE, true);

      if (firstBatch.events.length === 0) {
        if (firstBatch.nextCursor === 'wrap_refresh') {
          nextCursorRef.current = 'wrap_refresh';
        }
        setError('No events available');
        setIsLoadingNext(false);
        return;
      }

      eventCacheRef.current = firstBatch.events;
      nextCursorRef.current = firstBatch.nextCursor;
      windowStartIndexRef.current = 0;

      const windowItems = firstBatch.events.slice(0, Math.min(WINDOW_SIZE, firstBatch.events.length));
      setItems(windowItems);

      const centerIndex = Math.floor(windowItems.length / 2);
      setFocusIndex(centerIndex);
      focusedEventIdRef.current = windowItems[centerIndex]?.event_id;
      currentFocusEventIdRef.current = String(windowItems[centerIndex]?.event_id ?? '');

      setIsInitialized(true);  // show content now — spinner gone
      setIsLoadingNext(false);

      console.log('[useFeedBatches] ✅ Phase 1 done —', windowItems.length, 'items visible. Starting background prefetch...');

      // PHASE 2: Prefetch pages 2–5 in the background (ref mutations only, no re-renders)
      if (!firstBatch.nextCursor || firstBatch.nextCursor === 'wrap_refresh') return;

      let currentCursor = firstBatch.nextCursor;
      let fetchCount = 1;

      while (fetchCount < MAX_PREFETCH_PAGES) {
        const batch = await fetchEvents(currentCursor, BATCH_SIZE, true);

        if (batch.events.length === 0) {
          if (batch.nextCursor === 'wrap_refresh') {
            nextCursorRef.current = 'wrap_refresh';
          }
          console.log('[useFeedBatches] ⚠️ Background prefetch: no more items on page', fetchCount + 1);
          break;
        }

        eventCacheRef.current = [...eventCacheRef.current, ...batch.events];
        nextCursorRef.current = batch.nextCursor;
        currentCursor = batch.nextCursor;
        fetchCount++;

        console.log('[useFeedBatches] 📄 Background prefetch page', fetchCount, '— cache now', eventCacheRef.current.length, 'items');

        if (!currentCursor || currentCursor === 'wrap_refresh') {
          nextCursorRef.current = currentCursor;
          break;
        }
      }

      console.log('[useFeedBatches] ✅ Background prefetch complete —', eventCacheRef.current.length, 'items in cache');
    } catch (err) {
      setError('Failed to load events');
      console.error('[useFeedBatches] Initialization error:', err);
    } finally {
      setIsLoadingNext(false);
    }
  }, [enabled, isInitialized, fetchEvents, lat, lng, startDate, endDate]);

  /**
   * Load next batch (scroll down)
   * SIMPLIFIED: Sets minimal pendingScrollAdjustment with just what list needs
   * - Fetch new items from API
   * - Add to cache
   * - Shift window forward (remove top items, show next items)
   */
  const loadNext = useCallback(async () => {
    // we might get here from a horizontal edge-demand even if hasNext is false
    if (loadingLockRef.current) {
      console.log(
        "[useFeedBatches] loadNext skipped: loadingLockRef.current=true"
      );
      return;
    }
    if (isLoadingNext) {
      console.log("[useFeedBatches] loadNext skipped: isLoadingNext=true");
      return;
    }

    loadingLockRef.current = true;
    setIsLoadingNext(true);
    setIsShiftingWindow(true); // Block focus changes during shift
    console.log('[useFeedBatches] 🟣 SHIFT STARTING (loadNext) - window will move forward');

    try {
      // Capture preserved event ID before shift
      const preservedEventId = currentFocusEventIdRef.current || null;

      console.log(
        "[useFeedBatches] 📌 Preserved event ID before shift (may be forced by SmartEventList):",
        preservedEventId
      );

      // CRITICAL FIX: Check if we have enough items in cache before fetching from backend
      const currentCacheSize = eventCacheRef.current.length;
      const currentWindowEnd = windowStartIndexRef.current + WINDOW_SIZE;
      const itemsAfterWindow = currentCacheSize - currentWindowEnd;

      console.log('[useFeedBatches] 📊 Cache status:', {
        cacheSize: currentCacheSize,
        windowStart: windowStartIndexRef.current,
        windowEnd: currentWindowEnd,
        itemsAfterWindow: itemsAfterWindow,
        needsFetch: itemsAfterWindow < BATCH_SIZE
      });

      // Only fetch from backend if we don't have enough items in cache
      if (itemsAfterWindow < BATCH_SIZE && nextCursorRef.current) {
        const isWrapRefresh = nextCursorRef.current === 'wrap_refresh';

        if (isWrapRefresh) {
          console.log('🔄 [useFeedBatches] WRAP REFRESH CYCLE #', wrapCycleCountRef.current + 1, '- Pool exhausted, fetching with reset cursor');
        } else {
          console.log('[useFeedBatches] 🔄 Cache running low, fetching more from backend...');
        }

        const result = await fetchEvents(nextCursorRef.current, BATCH_SIZE);

        if (result.events.length === 0) {
          console.log('[useFeedBatches] ⚠️ Backend returned no more items');

          // INSTAGRAM-STYLE: If pool exhausted, immediately wrap_refresh for infinite scroll
          if (result.nextCursor === 'wrap_refresh') {
            console.log('🔄 [useFeedBatches] Pool exhausted - fetching with wrap_refresh (cycle #' + (wrapCycleCountRef.current + 1) + ')');

            // CRITICAL: Clear most exclusions BEFORE fetching to ensure we get items
            const currentlySeen = Array.from(recentlySeenIdsRef.current);
            console.log('🗑️ [useFeedBatches] Clearing exclusions before wrap (had', currentlySeen.length, 'items)');
            recentlySeenIdsRef.current = new Set(currentlySeen.slice(-5)); // Keep only last 5

            const wrapResult = await fetchEvents('wrap_refresh', BATCH_SIZE);

            if (wrapResult.events.length > 0) {
              // DEDUPLICATION: Filter out items already in cache
              const existingIds = new Set(
                eventCacheRef.current.map(e => String(e.event_id || e.id))
              );
              const uniqueEvents = wrapResult.events.filter(event => {
                const eventId = String(event.event_id || event.id);
                return !existingIds.has(eventId);
              });

              eventCacheRef.current = [...eventCacheRef.current, ...uniqueEvents];
              nextCursorRef.current = wrapResult.nextCursor;
              console.log('✅ [useFeedBatches] Wrap refresh successful! Added', uniqueEvents.length, '/', wrapResult.events.length, 'unique items. Total cache:', eventCacheRef.current.length, 'Wrap cycle:', wrapCycleCountRef.current);

              // If still no unique items after wrap, clear ALL exclusions and try once more
              if (uniqueEvents.length === 0) {
                console.log('⚠️ [useFeedBatches] All items were duplicates - clearing ALL exclusions for final retry');
                recentlySeenIdsRef.current = new Set(); // Clear everything

                const finalRetry = await fetchEvents('wrap_refresh', BATCH_SIZE);
                if (finalRetry.events.length > 0) {
                  eventCacheRef.current = [...eventCacheRef.current, ...finalRetry.events];
                  nextCursorRef.current = finalRetry.nextCursor;
                  console.log('✅ [useFeedBatches] Final retry successful! Added', finalRetry.events.length, 'items');
                }
              }
            } else {
              console.log('❌ [useFeedBatches] Wrap refresh returned 0 items - pool may be exhausted');
              // Don't return - let the window shift continue with what we have
            }
          } else {
            // No wrap refresh available, can't shift window
            console.log('⚠️ [useFeedBatches] No more items and no wrap refresh available');
            loadingLockRef.current = false;
            setIsLoadingNext(false);
            setIsShiftingWindow(false);
            return;
          }
        } else {
          // CLIENT-SIDE DEDUPLICATION: Prevent duplicates in cache
          const existingIds = new Set(
            eventCacheRef.current.map(e => String(e.event_id || e.id))
          );

          const newUniqueEvents = result.events.filter(event => {
            const eventId = String(event.event_id || event.id);
            if (existingIds.has(eventId)) {
              console.warn('[useFeedBatches] 🚫 Skipping duplicate in cache:', eventId);
              return false;
            }
            existingIds.add(eventId); // Add to set for next iteration
            return true;
          });

          // Add only unique events to cache
          eventCacheRef.current = [...eventCacheRef.current, ...newUniqueEvents];
          nextCursorRef.current = result.nextCursor;

          if (isWrapRefresh) {
            console.log('✅ [useFeedBatches] Wrap refresh successful! Added', newUniqueEvents.length, '/', result.events.length, 'unique items. Total cache:', eventCacheRef.current.length, 'Wrap cycle:', wrapCycleCountRef.current);
          } else {
            console.log('[useFeedBatches] Added', newUniqueEvents.length, '/', result.events.length, 'unique items to cache. Total:', eventCacheRef.current.length);
          }

          if (newUniqueEvents.length < result.events.length) {
            console.warn('[useFeedBatches] ⚠️ Filtered', result.events.length - newUniqueEvents.length, 'duplicate items from backend');
          }

          console.log('[useFeedBatches] 📍 Next cursor:', result.nextCursor ? 'Available ✓' : '⚠️ NULL - no more items from backend');
        }
      } else {
        console.log('[useFeedBatches] ✅ Using cached items, no backend fetch needed');
      }

      // Check if we have enough items to shift window
      const newWindowStart = windowStartIndexRef.current + BATCH_SIZE;
      if (newWindowStart >= eventCacheRef.current.length) {
        console.log('[useFeedBatches] ⚠️ Not enough items in cache to shift window');
        loadingLockRef.current = false;
        setIsLoadingNext(false);
        setIsShiftingWindow(false);
        return;
      }

      // SIMPLIFIED: Send minimal adjustment info - list will compute px itself
      const adjustmentId = ++scrollAdjustmentIdRef.current;
      setPendingScrollAdjustment({
        id: adjustmentId,
        kind: 'removeTop',
        removedCount: BATCH_SIZE,
        preservedEventId,
        adjustByPx: null, // List will compute px itself
        onApplied: onScrollAdjustmentApplied, // CRITICAL FIX: Pass callback
      });

      // Calculate focus index for window update
      const currentFocusIndex = focusedIndexRef.current;
      const fallbackFocusIndex = Math.max(0, currentFocusIndex - BATCH_SIZE);

      console.log('[useFeedBatches] 🎯 Fallback focus math: current', currentFocusIndex, '- shift', BATCH_SIZE, '= new', fallbackFocusIndex);

      // Update window (will try event ID first, then fallback to index)
      // newWindowStart is already defined above (line 485)
      updateWindowFromCache(newWindowStart, fallbackFocusIndex, preservedEventId);

      console.log('[useFeedBatches] ✅ Shifted window forward to start:', newWindowStart);

    } catch (err) {
      console.error('[useFeedBatches] Load next error:', err);
      setError('Failed to load more items');
    } finally {
      loadingLockRef.current = false;
      setIsLoadingNext(false);
      setTimeout(() => {
        setIsShiftingWindow(false);
        console.log('[useFeedBatches] 🟢 SHIFT COMPLETED (loadNext) - window shift finished');
      }, 30); // INSTAGRAM-STYLE: Faster unlock (was 80ms) - minimize dead zones
    }
  }, [fetchEvents, isLoadingNext, updateWindowFromCache]);

  /**
   * Load previous batch (scroll up)
   * SIMPLIFIED: Sets minimal pendingScrollAdjustment with just what list needs
   * - Use cached events (no API call needed)
   * - Shift window backward (show previous items, remove last items)
   */
  const loadPrevious = useCallback(async () => {
    if (loadingLockRef.current || isLoadingPrevious) {
      return;
    }

    const currentWindowStart = windowStartIndexRef.current;

    // Check if there are previous items in cache
    if (currentWindowStart === 0) {
      console.log('[useFeedBatches] Already at the beginning of cache');
      return;
    }

    loadingLockRef.current = true;
    setIsLoadingPrevious(true);
    setIsShiftingWindow(true); // Block focus changes during shift
    console.log('[useFeedBatches] 🟣 SHIFT STARTING (loadPrevious) - window will move backward');

    try {
      console.log('[useFeedBatches] 🔄 Loading previous batch from cache...');

      // Capture preserved event ID before shift
      const preservedEventId = currentFocusEventIdRef.current || null;

      console.log('[useFeedBatches] 📌 Preserved event ID before shift:', preservedEventId);

      // Calculate new window position
      const newWindowStart = Math.max(0, currentWindowStart - BATCH_SIZE);

      // SIMPLIFIED: Send minimal adjustment info - list will compute px itself
      const adjustmentId = ++scrollAdjustmentIdRef.current;
      const prependedCount = currentWindowStart - newWindowStart;
      setPendingScrollAdjustment({
        id: adjustmentId,
        kind: 'prepend',
        prependedCount,
        preservedEventId,
        adjustByPx: null, // List will compute px itself
        onApplied: onScrollAdjustmentApplied, // CRITICAL FIX: Pass callback
      });

      // Calculate new focus index as fallback: currentIndex + shift amount
      const currentFocusIndex = focusedIndexRef.current;
      const fallbackFocusIndex = currentFocusIndex + prependedCount;

      console.log('[useFeedBatches] 🎯 Fallback focus math: current', currentFocusIndex, '+ shift', prependedCount, '= new', fallbackFocusIndex);

      // Update window (will try event ID first, then fallback to index)
      updateWindowFromCache(newWindowStart, fallbackFocusIndex, preservedEventId);

      console.log('[useFeedBatches] ✅ Shifted window backward to start:', newWindowStart);

    } catch (err) {
      console.error('[useFeedBatches] Load previous error:', err);
      setError('Failed to load previous items');
    } finally {
      loadingLockRef.current = false;
      setIsLoadingPrevious(false);
      setTimeout(() => {
        setIsShiftingWindow(false);
        console.log('[useFeedBatches] 🟢 SHIFT COMPLETED (loadPrevious) - window shift finished');
      }, 30); // INSTAGRAM-STYLE: Faster unlock (was 80ms) - minimize dead zones
    }
  }, [isLoadingPrevious, updateWindowFromCache]);

  /**
   * Handle focus index change from scroll
   * TASK 1: Now accepts both index and eventId
   * ENHANCED: Prevents focus changes during window shifts OR any loading + handles fast scrolling
   */
  const handleFocusChange = useCallback((newIndex, eventId) => {
    // CRITICAL: Ignore ALL focus changes during window shifts OR loading
    if (isShiftingWindow || isLoadingNext || isLoadingPrevious) {
      console.log('[useFeedBatches] 🚫 BLOCKED focus change to index', newIndex, '- shift/load in progress (isShiftingWindow:', isShiftingWindow, 'isLoadingNext:', isLoadingNext, 'isLoadingPrevious:', isLoadingPrevious, ')');
      return;
    }

    setFocusIndex(newIndex);
    const currentEvent = items[newIndex];

    // TASK 1: Store both index and event ID
    focusedIndexRef.current = newIndex;

    // Prefer passed eventId, fallback to deriving from item
    const finalEventId = eventId || currentEvent?.event_id || currentEvent?.id;
    if (finalEventId) {
      currentFocusEventIdRef.current = String(finalEventId);
      focusedEventIdRef.current = String(finalEventId); // Keep old ref for compatibility
    }

    console.log('[useFeedBatches] Focus changed to index:', newIndex, '/', items.length, 'event:', currentEvent?.event_name?.substring(0, 30), 'ID:', finalEventId);

    // INSTAGRAM-STYLE AGGRESSIVE LOADING: Trigger early for seamless infinite scroll
    // Dynamic calculation: always trigger when 7 items from bottom (23% buffer)
    const TRIGGER_DOWN_INDEX = Math.max(1, items.length - 7);
    const TRIGGER_UP_INDEX = 7;  // Trigger when 7 items from top (symmetrical)
    const LAST_INDEX = items.length - 1;

    // ⬇️ TRIGGER DOWN: Load next batch aggressively for smooth infinite scroll
    if (newIndex >= TRIGGER_DOWN_INDEX && !isLoadingNext) {
      if (newIndex === LAST_INDEX) {
        console.log('[useFeedBatches] ⚡ FAST SCROLL: User reached LAST item - loading IMMEDIATELY');
      } else {
        console.log('[useFeedBatches] ⬇️ Instagram-style trigger at index', newIndex, '/', items.length, '(7 items from bottom)');
      }
      // Always try to load - don't check cursor (might have more via wrap_refresh)
      loadNext();
    }
    // ⬆️ TRIGGER UP: Load previous batch when at index <= 7
    else if (newIndex <= TRIGGER_UP_INDEX && !isLoadingPrevious && windowStartIndexRef.current > 0) {
      console.log('[useFeedBatches] ⬆️ Instagram-style trigger at index', newIndex, '/', items.length, '(7 items from top)');
      loadPrevious();
    }
  }, [items, isLoadingNext, isLoadingPrevious, isShiftingWindow, loadNext, loadPrevious]);

  /**
   * Handle scroll adjustment applied by list
   * Clears the pending adjustment when list confirms it's done
   */
  const onScrollAdjustmentApplied = useCallback((id) => {
    setPendingScrollAdjustment((prev) => (prev && prev.id === id ? null : prev));
    setIsShiftingWindow(false);
    console.log('[useFeedBatches] ✅ Scroll adjustment', id, 'applied and cleared');
  }, []);

  /**
   * Reset/refresh the feed
   */
  const reset = useCallback(() => {
    setItems([]);
    setFocusIndex(7);
    setIsInitialized(false);
    nextCursorRef.current = null;
    focusedEventIdRef.current = null;
    currentFocusEventIdRef.current = null;
    eventCacheRef.current = [];
    windowStartIndexRef.current = 0;
    seenIdsRef.current = new Set();
    recentlySeenIdsRef.current = new Set(); // Clear recently seen on full reset
    wrapCycleCountRef.current = 0; // Reset wrap counter
    isInRotationModeRef.current = false; // Reset to loading new content mode
    setError(null);
    setPendingScrollAdjustment(null);
  }, []);

  /**
   * Reset and re-initialize the feed (for searchQuery changes)
   */
  const resetAndInitialize = useCallback(async () => {
    reset();
    if (enabled) {
      await initialize();
    }
  }, [reset, initialize, enabled]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    if (enabled && !isInitialized) {
      initialize();
    }
  }, [enabled, isInitialized, initialize]);

  return {
    // State
    items,
    focusIndex,
    focusIsAuthoritative, // NEW: Whether focus was truly preserved (vs weak fallback)
    isLoadingNext,
    isLoadingPrevious,
    isInitialized,
    error,
    isShiftingWindow, // NEW: Expose for UI to prevent interference
    pendingScrollAdjustment, // TASK 2: Expose scroll adjustment info
    wasLastShiftAuthoritative: lastShiftPreservedByEventIdRef.current, // Part 3: Was last shift preserved by event ID

    // Status indicators
    hasNext: !!nextCursorRef.current,
    hasPrevious: windowStartIndexRef.current > 0,

    // Methods
    handleFocusChange, // TASK 1: Now accepts (index, eventId)
    loadNext,
    loadPrevious,
    reset,
    initialize,
    resetAndInitialize,   // NEW: Reset and re-initialize on searchQuery change
    onScrollAdjustmentApplied // NEW: Callback for list to confirm adjustment applied
  };
};
